import React, { useEffect, useState } from 'react';
import { ref, onValue, set } from 'firebase/database';
import { database } from '../firebaseConfig';
import { ownerOf } from '../services/recordAccess';
import { DEFAULT_PIPELINE_GOAL, isActivePipeline, goalFor } from '../shared/performanceMath';

// Pipeline goal tracker: every agent should keep at least 20 people in their pipeline.
// The goal lives at system/pipelineGoal { default, perAgent: { uid: n } } (admin-write, everyone-read).
// Nothing is written there until an admin presses Save, so the code default (20) applies until then.
// Goal math lives in src/shared/performanceMath.js (also used by the MCP server).
export { DEFAULT_PIPELINE_GOAL, isActivePipeline, goalFor };

const wrap = { background: '#fff', border: '1px solid #e5e5e5', borderRadius: 10, padding: 16, marginBottom: 12, maxWidth: '100%', boxSizing: 'border-box', overflowWrap: 'anywhere' };
const barBg = { height: 12, background: '#eee', borderRadius: 6, overflow: 'hidden', margin: '10px 0 6px' };
const num = { width: 64, padding: 6, fontSize: 16, border: '1px solid #ccc', borderRadius: 6, boxSizing: 'border-box' };
const btn = { padding: '6px 12px', fontSize: 14, border: '1px solid #b8903a', background: '#d9b357', borderRadius: 6, cursor: 'pointer', fontWeight: 600 };

function Bar({ count, goal }) {
  const pct = Math.min(100, Math.round((count / goal) * 100));
  const hit = count >= goal;
  return <div style={barBg}><div style={{ width: `${pct}%`, height: '100%', background: hit ? '#2e7d32' : '#B8962E', transition: 'width .3s' }} /></div>;
}

export default function PipelineGoal({ uid, rows, isAdmin, team = [] }) {
  const [cfg, setCfg] = useState(null);
  const [edit, setEdit] = useState(null);
  const [msg, setMsg] = useState('');
  useEffect(() => onValue(ref(database, 'system/pipelineGoal'), (s) => setCfg(s.val() || {}), () => setCfg({})), []);

  const active = (rows || []).filter(isActivePipeline);
  const mine = active.filter((r) => ownerOf(r) === uid || r.assignedAgentId === uid).length;
  const goal = goalFor(cfg, uid);
  const left = Math.max(0, goal - mine);
  const hit = mine >= goal;

  const counts = {};
  active.forEach((r) => { const o = r.assignedAgentId || ownerOf(r) || ''; counts[o] = (counts[o] || 0) + 1; });
  const agents = team.filter((t) => counts[t.uid] || !['Admin', 'TC', 'Marketing Manager', 'Local Assistant'].includes(t.role));

  const save = async () => {
    try {
      const perAgent = Object.fromEntries(Object.entries(edit.perAgent || {}).filter(([, v]) => Number(v) > 0).map(([k, v]) => [k, Number(v)]));
      await set(ref(database, 'system/pipelineGoal'), { default: Number(edit.default) > 0 ? Number(edit.default) : DEFAULT_PIPELINE_GOAL, perAgent, updatedAt: new Date().toISOString() });
      setEdit(null); setMsg('Goal saved.');
    } catch (e) { setMsg('Could not save the goal.'); }
  };

  return (
    <div style={wrap}>
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6, alignItems: 'baseline' }}>
        <strong style={{ fontSize: 16 }}>Pipeline goal</strong>
        <span style={{ fontSize: 14 }}><strong>{mine}</strong> of {goal} people</span>
      </div>
      <Bar count={mine} goal={goal} />
      {hit
        ? <p style={{ margin: 0, fontSize: 14, color: '#2e7d32', fontWeight: 600 }}>🎉 Goal reached! {mine > goal ? `${mine - goal} over your goal. ` : ''}Keep the conversations going.</p>
        : <p style={{ margin: 0, fontSize: 14 }}><strong>{left} more</strong> to reach your goal. {left <= 3 ? "You're so close!" : 'Every conversation counts. Add one today.'}</p>}

      {isAdmin && (
        <details style={{ marginTop: 12 }}>
          <summary style={{ cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Team breakdown</summary>
          <div style={{ marginTop: 8 }}>
            {agents.map((t) => {
              const c = counts[t.uid] || 0; const g = goalFor(cfg, t.uid);
              return (
                <div key={t.uid} style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 14 }}>
                    <span>{t.name}</span><span style={{ whiteSpace: 'nowrap', color: c >= g ? '#2e7d32' : '#555' }}>{c} / {g}{c >= g ? ' ✓' : ''}</span>
                  </div>
                  <Bar count={c} goal={g} />
                </div>
              );
            })}
            {!edit && <button type="button" style={btn} onClick={() => setEdit({ default: cfg?.default || DEFAULT_PIPELINE_GOAL, perAgent: { ...(cfg?.perAgent || {}) } })}>Edit goals</button>}
            {edit && (
              <div style={{ marginTop: 8 }}>
                <label style={{ fontSize: 14 }}>Team goal <input type="number" min="1" style={num} value={edit.default} onChange={(e) => setEdit({ ...edit, default: e.target.value })} /></label>
                <p style={{ fontSize: 13, color: '#666', margin: '8px 0 4px' }}>Per-agent goal (leave blank to use the team goal):</p>
                {agents.map((t) => (
                  <div key={t.uid} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', marginBottom: 4, fontSize: 14 }}>
                    <span>{t.name}</span>
                    <input type="number" min="1" style={num} value={edit.perAgent[t.uid] || ''} onChange={(e) => setEdit({ ...edit, perAgent: { ...edit.perAgent, [t.uid]: e.target.value } })} />
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}><button type="button" style={btn} onClick={save}>Save</button><button type="button" style={{ ...btn, background: '#fff' }} onClick={() => setEdit(null)}>Cancel</button></div>
              </div>
            )}
          </div>
        </details>
      )}
      {msg && <p style={{ fontSize: 13, color: '#2e7d32', margin: '8px 0 0' }}>{msg}</p>}
    </div>
  );
}
